/**
 * PassengerAiBooking — natural-language booking inside the Passenger App.
 *
 * Reuses the SHARED AI engine (parse_booking, channel="passenger"), the EXISTING
 * /passenger/estimate (read-only quote) and /passenger/request-ride (the same
 * booking endpoint the manual form uses). The passenger describes their trip;
 * the AI extracts a draft and asks for anything missing. AI address TEXT is never
 * auto-geocoded into a live booking — the passenger confirms pickup & drop-off via
 * the existing Google Places autocomplete before the quote + Confirm appear.
 * On confirm, the created ride is tagged ai_drafted / source="ai/passenger".
 * No dispatch / lifecycle / pricing / payment changes.
 */
import React, { useState, useRef, useEffect } from "react";
import {
  Wand2, Send, Loader2, MapPin, Clock, CreditCard, Banknote, Briefcase, Users,
  Accessibility, RotateCcw, CheckCircle2, Route, Car, Gauge, MessageSquare,
} from "lucide-react";
import { Button } from "../ui/button";
import { api, formatApiError } from "../../lib/api";
import { PlaceAutocomplete } from "../PlaceAutocomplete";
import { FareBreakdown } from "./FareBreakdown";
import { canConfirmAiBooking } from "./aiBookingGate";
import { toast } from "sonner";

const newSessionId = () =>
  (window.crypto?.randomUUID?.() || `pax-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const SCHEDULE_LABEL = (s) => {
  if (!s || s === "asap") return "As soon as possible";
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const EXAMPLES = [
  "Pick me up from Basingstoke Station to Heathrow Terminal 3 tomorrow at 5am",
  "I need a taxi home in 20 minutes",
  "Book my usual airport journey for Friday",
];

const Field = ({ icon: Icon, label, value, missing }) => (
  <div className={`flex items-start gap-2 px-2.5 py-1.5 rounded-lg border ${missing ? "border-dashed border-[#F59E0B] bg-[#FFFBEB]" : "border-border bg-white"}`}>
    <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${missing ? "text-[#B45309]" : "text-muted-foreground"}`} />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
      <div className={`text-sm truncate ${missing ? "text-[#B45309] italic" : "font-medium"}`}>{missing ? "Needed" : (value || "—")}</div>
    </div>
  </div>
);

export const PassengerAiBooking = ({ user, onBooked }) => {
  const sessionIdRef = useRef(newSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState(null);

  const [pickupSel, setPickupSel] = useState(null);   // {name,lat,lng}
  const [dropoffSel, setDropoffSel] = useState(null);
  const lastPickupText = useRef(null);
  const lastDropoffText = useRef(null);

  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [estimate, setEstimate] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [booking, setBooking] = useState(false);
  const scrollRef = useRef(null);

  const isAccount = !!user?.account_company;
  const hasDog = draft ? /\b(dog|pet|puppy|guide dog|assistance dog)\b/i.test([draft.notes, draft.accessibility].filter(Boolean).join(" ")) : false;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // Seed the address-confirmation fields from the AI's text. Resetting lat/lng
  // forces the passenger to re-confirm on the map (never auto-geocoded).
  useEffect(() => {
    const t = draft?.pickup_text;
    if (t && t !== lastPickupText.current) { lastPickupText.current = t; setPickupSel({ name: t }); }
  }, [draft?.pickup_text]);
  useEffect(() => {
    const t = draft?.dropoff_text;
    if (t && t !== lastDropoffText.current) { lastDropoffText.current = t; setDropoffSel({ name: t }); }
  }, [draft?.dropoff_text]);

  useEffect(() => {
    if (draft?.payment_method) setPaymentMethod(draft.payment_method);
  }, [draft?.payment_method]);

  // Read-only quote via the EXISTING passenger estimator, once BOTH addresses
  // are confirmed on the map (have coordinates).
  useEffect(() => {
    if (!(pickupSel?.lat != null && dropoffSel?.lat != null)) { setEstimate(null); return; }
    let ignore = false;
    setQuoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/passenger/estimate", {
          pickup_address: pickupSel.name, pickup_lat: pickupSel.lat, pickup_lng: pickupSel.lng,
          dropoff_address: dropoffSel.name, dropoff_lat: dropoffSel.lat, dropoff_lng: dropoffSel.lng,
          vehicle_type: draft?.vehicle_type || "car",
          has_dog: hasDog,
        });
        if (!ignore) setEstimate(data);
      } catch (e) {
        if (!ignore) { setEstimate(null); }
      } finally {
        if (!ignore) setQuoteLoading(false);
      }
    }, 300);
    return () => { ignore = true; clearTimeout(t); };
  }, [pickupSel?.lat, pickupSel?.lng, dropoffSel?.lat, dropoffSel?.lng, draft?.vehicle_type, hasDog]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "passenger", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/passenger/ai-booking/parse", { session_id: sessionIdRef.current, message: msg });
      setDraft(data.draft);
      setMeta({ missing_fields: data.missing_fields, ready_for_review: data.ready_for_review, confidence: data.confidence });
      setMessages((m) => [...m, { role: "assistant", text: data.follow_up_question || data.assistant_message || "Got it." }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(e), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    sessionIdRef.current = newSessionId();
    setMessages([]); setDraft(null); setMeta(null); setInput("");
    setPickupSel(null); setDropoffSel(null); setEstimate(null); setDetailsOpen(false);
    lastPickupText.current = null; lastDropoffText.current = null;
  };

  const confirmBooking = async () => {
    if (booking) return;
    if (!(pickupSel?.lat != null && dropoffSel?.lat != null)) {
      toast.error("Please confirm both pickup and destination on the map first.");
      return;
    }
    // Resolve schedule: future ISO → scheduled; "asap"/null → now.
    let scheduledIso = null;
    const s = draft?.scheduled_for;
    if (s && s !== "asap") {
      const dt = new Date(s);
      if (!isNaN(dt.getTime()) && dt.getTime() - Date.now() >= 15 * 60 * 1000) scheduledIso = dt.toISOString();
    }
    setBooking(true);
    try {
      const { data: ride } = await api.post("/passenger/request-ride", {
        pickup_address: pickupSel.name, pickup_lat: pickupSel.lat, pickup_lng: pickupSel.lng,
        dropoff_address: dropoffSel.name, dropoff_lat: dropoffSel.lat, dropoff_lng: dropoffSel.lng,
        payment_method: paymentMethod,
        vehicle_type: draft?.vehicle_type || "car",
        passenger_count: draft?.passenger_count || 1,
        has_dog: hasDog,
        driver_notes: draft?.notes || draft?.accessibility || undefined,
        ...(scheduledIso ? { scheduled_for: scheduledIso } : {}),
      });
      // Tag the created ride with the AI audit metadata (additive only).
      // Awaited (errors swallowed) so the audit reliably lands before we reset.
      await api.post(`/passenger/ai-booking/confirmed?session_id=${encodeURIComponent(sessionIdRef.current)}`, { ride_id: ride.id }).catch(() => {});
      toast.success(scheduledIso ? "Ride scheduled — we'll dispatch a driver 15 min before pickup." : "Searching for nearby drivers…");
      onBooked?.({ scheduled: !!scheduledIso });
      startOver();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBooking(false);
    }
  };

  const isMissing = (f) => meta?.missing_fields?.includes(f);
  const bothConfirmed = pickupSel?.lat != null && dropoffSel?.lat != null;

  return (
    <div className="space-y-3" data-testid="passenger-ai-booking">
      <div className="flex items-start gap-2">
        <span className="w-8 h-8 rounded-lg bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
          <Wand2 className="w-4 h-4" />
        </span>
        <div>
          <div className="font-heading font-black text-base leading-tight">AI Booking Assistant</div>
          <div className="text-xs text-muted-foreground leading-tight">Describe your journey naturally. I'll prepare everything before you confirm.</div>
        </div>
      </div>

      {/* Chat */}
      <div ref={scrollRef} className="max-h-[260px] overflow-y-auto space-y-2 border border-border rounded-xl p-3 bg-[#FAFAFB]" data-testid="passenger-ai-messages">
        {messages.length === 0 && (
          <div className="text-sm text-muted-foreground space-y-2">
            <p className="flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Where are you travelling? Try:</p>
            {EXAMPLES.map((ex, i) => (
              <button key={i} onClick={() => send(ex)} className="block w-full text-left px-3 py-2 rounded-lg border border-border bg-white hover:border-[#002FA7] transition-colors text-foreground text-xs" data-testid={`passenger-ai-example-${i}`}>
                “{ex}”
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "passenger" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
              m.role === "passenger" ? "bg-[#002FA7] text-white rounded-br-sm"
              : m.error ? "bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA] rounded-bl-sm"
              : "bg-white border border-border rounded-bl-sm"}`} data-testid={`passenger-ai-msg-${m.role}`}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="e.g. Basingstoke Station to Heathrow tomorrow 5am"
          className="flex-1 outline-none bg-secondary/40 rounded-full px-4 py-2 text-sm border border-border"
          data-testid="passenger-ai-input"
          disabled={loading}
        />
        <Button onClick={() => send()} disabled={loading || !input.trim()} className="rounded-full h-9 w-9 p-0" data-testid="passenger-ai-send">
          <Send className="w-4 h-4" />
        </Button>
        {messages.length > 0 && (
          <button onClick={startOver} className="p-2 rounded-full hover:bg-secondary" title="Start over" data-testid="passenger-ai-reset">
            <RotateCcw className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Draft */}
      {draft && (
        <div className="border border-border rounded-xl bg-white p-3 space-y-2" data-testid="passenger-ai-draft">
          <div className="flex items-center justify-between">
            <span className="label-mini text-muted-foreground">YOUR TRIP</span>
            {meta?.confidence != null && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="passenger-ai-confidence">
                <Gauge className="w-3 h-3" /> {Math.round(meta.confidence * 100)}% confident
              </span>
            )}
          </div>

          {/* Address confirmation — Google Places, required before booking */}
          <div className="space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-[#10B981]" /> Confirm pickup {pickupSel?.lat != null && <CheckCircle2 className="w-3 h-3 text-[#16A34A]" />}
              </div>
              <PlaceAutocomplete value={pickupSel} onChange={setPickupSel} seedSearch placeholder="Confirm pickup address" testId="passenger-ai-pickup" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-[#EF4444]" /> Confirm destination {dropoffSel?.lat != null && <CheckCircle2 className="w-3 h-3 text-[#16A34A]" />}
              </div>
              <PlaceAutocomplete value={dropoffSel} onChange={setDropoffSel} seedSearch placeholder="Confirm destination address" testId="passenger-ai-dropoff" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field icon={Clock} label="When" value={SCHEDULE_LABEL(draft.scheduled_for)} />
            <Field icon={Users} label="Passengers" value={String(draft.passenger_count || 1)} />
            <Field icon={Car} label="Vehicle" value={draft.vehicle_type === "bus" ? "Minibus (8)" : "Car (4)"} />
            <Field icon={Accessibility} label="Notes" value={[draft.accessibility, draft.notes, draft.is_return ? "Return" : null].filter(Boolean).join(" · ") || "—"} />
          </div>

          {/* Payment */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Payment</div>
            <div className={`grid gap-px bg-border border border-border ${isAccount ? "grid-cols-3" : "grid-cols-2"}`}>
              <button type="button" onClick={() => setPaymentMethod("cash")} className={`py-2 flex items-center justify-center gap-1.5 text-xs font-bold transition-colors ${paymentMethod === "cash" ? "bg-[#0A0A0A] text-white" : "bg-white"}`} data-testid="passenger-ai-pay-cash"><Banknote className="w-3.5 h-3.5" /> CASH</button>
              <button type="button" onClick={() => setPaymentMethod("card")} className={`py-2 flex items-center justify-center gap-1.5 text-xs font-bold transition-colors ${paymentMethod === "card" ? "bg-[#0A0A0A] text-white" : "bg-white"}`} data-testid="passenger-ai-pay-card"><CreditCard className="w-3.5 h-3.5" /> CARD</button>
              {isAccount && (
                <button type="button" onClick={() => setPaymentMethod("account")} className={`py-2 flex items-center justify-center gap-1.5 text-xs font-bold transition-colors ${paymentMethod === "account" ? "bg-[#002FA7] text-white" : "bg-white"}`} data-testid="passenger-ai-pay-account"><Briefcase className="w-3.5 h-3.5" /> ACCOUNT</button>
              )}
            </div>
          </div>

          {/* Quote */}
          <div className="pt-2 border-t border-dashed border-border" data-testid="passenger-ai-quote">
            {!bothConfirmed ? (
              <p className="text-[11px] text-muted-foreground italic">Confirm both addresses above to see your fare.</p>
            ) : quoteLoading ? (
              <div className="space-y-2" data-testid="passenger-ai-fare-skeleton">
                <div className="flex items-center gap-2 text-xs font-bold text-[#002FA7]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Getting your fare…</div>
                <div className="grid grid-cols-3 gap-2">
                  {["Dist", "Time", "Fare"].map((l) => (
                    <div key={l} className="bg-secondary/50 rounded-lg py-2 px-1 space-y-1.5">
                      <div className="text-[10px] uppercase text-muted-foreground text-center">{l}</div>
                      <div className="h-4 w-full rounded bg-border animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            ) : estimate ? (
              <>
                {estimate.is_fixed_route && (
                  <div className="text-[11px] font-bold text-[#002FA7] mb-1">FIXED FARE · {estimate.fixed_route_name?.toUpperCase()}</div>
                )}
                {estimate.surge_multiplier > 1.0 && (
                  <div className="text-[11px] font-bold text-[#B45309] mb-1">Surge ×{estimate.surge_multiplier} in this area</div>
                )}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-secondary/50 rounded-lg py-2"><div className="text-[10px] uppercase text-muted-foreground flex items-center justify-center gap-1"><Route className="w-3 h-3" />Dist</div><div className="font-bold text-sm" data-testid="passenger-ai-distance">{estimate.distance_mi ?? estimate.distance_km} mi</div></div>
                  <div className="bg-secondary/50 rounded-lg py-2"><div className="text-[10px] uppercase text-muted-foreground flex items-center justify-center gap-1"><Clock className="w-3 h-3" />Time</div><div className="font-bold text-sm" data-testid="passenger-ai-duration">{Math.round(estimate.duration_min)} min</div></div>
                  <div className="bg-[#002FA7] text-white rounded-lg py-2"><div className="text-[10px] uppercase text-white/70">Fare</div><div className="font-heading font-black text-base" data-testid="passenger-ai-fare">£{estimate.fare}</div></div>
                </div>
                {estimate.fare_label && <p className="mt-1 text-[11px] text-muted-foreground" data-testid="passenger-ai-fare-label">{estimate.fare_label}</p>}
                <button type="button" onClick={() => setDetailsOpen((v) => !v)} className="mt-2 text-xs font-semibold text-[#002FA7] hover:underline" data-testid="passenger-ai-details-toggle">
                  {detailsOpen ? "Hide" : "Show"} fare details
                </button>
                {detailsOpen && estimate.breakdown && (
                  <div className="mt-1.5 border border-border rounded-lg p-2.5 bg-secondary/30" data-testid="passenger-ai-details">
                    <FareBreakdown b={estimate.breakdown} compact />
                  </div>
                )}
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground italic" data-testid="passenger-ai-estimate-unavailable">Price unavailable right now — we couldn't plan a road route. Please try again shortly or call us.</p>
            )}
          </div>

          {/* CTA is gated on the booking essentials only: not already booking and
              BOTH addresses confirmed on the map (coordinates required by
              request-ride). The `estimate` is a PASSIVE display quote — the
              backend recomputes the fare authoritatively — so an estimate
              hiccup must NEVER block booking (previously `!estimate` here
              dead-locked the Confirm button when the quote was unavailable). */}
          {/* Sticky so the primary action is ALWAYS visible & tappable — even
              on small/mobile viewports where a long draft could otherwise push
              it below the fold or under other chrome. Gated on booking
              essentials only (not the passive estimate). */}
          <div className="sticky bottom-2 z-10 pt-1 -mx-3 px-3 bg-gradient-to-t from-white via-white to-transparent">
            <Button onClick={confirmBooking} disabled={!canConfirmAiBooking({ booking, bothConfirmed })} className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="passenger-ai-confirm">
              {booking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {draft?.scheduled_for && draft.scheduled_for !== "asap" ? "Confirm & schedule" : "Confirm booking"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengerAiBooking;
